// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
import * as React from 'react';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { CatalogUrlPlaceholderDto } from '@greenlight/shared';
import { applyUrlPlaceholders } from '@greenlight/shared';

export interface UrlPlaceholderFieldsProps {
  placeholders: readonly CatalogUrlPlaceholderDto[];
  values: Readonly<Record<string, string>>;
  errors?: Readonly<Record<string, string>>;
  baseUrlTemplate: string;
  tokenUrlTemplate: string;
  authorizeUrlTemplate?: string;
  onChange: (token: string, value: string) => void;
}

export function UrlPlaceholderFields({
  placeholders,
  values,
  errors,
  baseUrlTemplate,
  tokenUrlTemplate,
  authorizeUrlTemplate = '',
  onChange,
}: UrlPlaceholderFieldsProps): React.JSX.Element | null {
  if (placeholders.length === 0) return null;

  const resolvedBase = applyUrlPlaceholders(baseUrlTemplate, values, placeholders);
  const resolvedToken = tokenUrlTemplate
    ? applyUrlPlaceholders(tokenUrlTemplate, values, placeholders)
    : '';
  const resolvedAuthorize = authorizeUrlTemplate
    ? applyUrlPlaceholders(authorizeUrlTemplate, values, placeholders)
    : '';
  const showBasePreview = baseUrlTemplate.includes('{');
  const showTokenPreview = tokenUrlTemplate.includes('{');
  const showAuthorizePreview = authorizeUrlTemplate.includes('{');

  return (
    <div className="space-y-4" data-testid="url-placeholders">
      {placeholders.map((placeholder) => {
        const id = `reg-placeholder-${placeholder.token}`;
        const error = errors?.[placeholder.token];
        return (
          <div key={placeholder.token} className="space-y-1.5">
            <Label htmlFor={id}>{placeholder.label}</Label>
            <Input
              id={id}
              value={values[placeholder.token] ?? ''}
              onChange={(event) => onChange(placeholder.token, event.target.value)}
              placeholder={placeholder.example ?? undefined}
              className="font-mono"
              autoComplete="off"
            />
            {error ? (
              <p className="text-[11.5px] text-destructive">{error}</p>
            ) : placeholder.hint ? (
              <p className="text-[11.5px] leading-snug text-muted-foreground">{placeholder.hint}</p>
            ) : null}
          </div>
        );
      })}
      {(showBasePreview || showTokenPreview || showAuthorizePreview) && (
        <div
          className="space-y-1.5 rounded-gl border border-border bg-muted/30 px-3 py-2.5"
          data-testid="url-placeholder-preview"
        >
          <p className="text-[11.5px] font-medium text-foreground">Resolved URLs</p>
          {showBasePreview && (
            <p
              className="text-[11.5px] leading-snug text-muted-foreground"
              data-testid="url-preview-base"
            >
              Base URL: <MonoValue wrap>{resolvedBase || baseUrlTemplate}</MonoValue>
            </p>
          )}
          {showAuthorizePreview && (
            <p
              className="text-[11.5px] leading-snug text-muted-foreground"
              data-testid="url-preview-authorize"
            >
              Authorize URL:{' '}
              <MonoValue className="text-[11.5px]" wrap>
                {resolvedAuthorize || authorizeUrlTemplate}
              </MonoValue>
            </p>
          )}
          {showTokenPreview && (
            <p
              className="text-[11.5px] leading-snug text-muted-foreground"
              data-testid="url-preview-token"
            >
              Token URL: <MonoValue wrap>{resolvedToken || tokenUrlTemplate}</MonoValue>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
